// Mobile/touch environment helpers + iOS keyboard handling.
//
// Two things the web platform still can't do declaratively and that separate
// an "app" from a "web page" on a phone:
//
//   1. Coarse-pointer detection, so we don't pop the soft keyboard by
//      auto-focusing the composer on every mount/thread-switch (ChatGPT does
//      NOT auto-focus on mobile).
//   2. iOS soft-keyboard handling. On iOS Safari the keyboard OVERLAYS the
//      layout — `100dvh` / `h-dvh` does NOT shrink — so the composer ends up
//      hidden behind the keyboard. The only fix is a `visualViewport` listener
//      that publishes the covered height as a CSS var the layout rides above.
//      (Android/Chrome honors `interactive-widget=resizes-content`, set in the
//      viewport meta, so this is a no-op there.)

import { useEffect, useState } from 'react';

const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'date',
  'datetime-local',
  'file',
  'hidden',
  'image',
  'month',
  'radio',
  'range',
  'reset',
  'submit',
  'time',
  'week',
]);

const MIN_KEYBOARD_OCCLUSION_PX = 100;
const MIN_KEYBOARD_OCCLUSION_RATIO = 0.15;

type ElementLike = {
  tagName?: string;
  type?: string;
  isContentEditable?: boolean;
  contentEditable?: string;
  disabled?: boolean;
  readOnly?: boolean;
};

export type KeyboardViewportMeasurement = {
  layoutHeight: number;
  viewportHeight: number;
  viewportOffsetTop: number;
  viewportScale: number;
  hasTextEntryFocus: boolean;
};

/** True when the focused element can summon the on-screen text keyboard. */
export function isTextEntryElement(element: ElementLike | null): boolean {
  if (!element || element.disabled || element.readOnly) return false;
  const tagName = element.tagName?.toLowerCase();
  if (tagName === 'textarea') return true;
  if (element.isContentEditable && element.contentEditable?.toLowerCase() !== 'false') return true;
  if (tagName !== 'input') return false;
  return !NON_TEXT_INPUT_TYPES.has((element.type || 'text').toLowerCase());
}

/**
 * Convert VisualViewport geometry into keyboard overlap. A viewport change is
 * keyboard-shaped only while a text control is focused and the page is not
 * pinch-zoomed; browser chrome, rotation, scrolling and zoom otherwise stay at
 * zero. Exported as a pure seam so the mobile contract is directly testable.
 */
export function keyboardInsetFromViewport(measurement: KeyboardViewportMeasurement): number {
  if (!measurement.hasTextEntryFocus || Math.abs(measurement.viewportScale - 1) > 0.01) return 0;
  const covered = Math.max(
    0,
    measurement.layoutHeight - measurement.viewportHeight - measurement.viewportOffsetTop,
  );
  // Safari's collapsing browser chrome can move VisualViewport by ~50–90px
  // even while an editor remains focused. A software keyboard occupies a
  // materially larger share of the layout viewport on supported iPhones and
  // iPads, so require both an absolute and proportional occlusion floor.
  const keyboardFloor = Math.max(
    MIN_KEYBOARD_OCCLUSION_PX,
    measurement.layoutHeight * MIN_KEYBOARD_OCCLUSION_RATIO,
  );
  return covered >= keyboardFloor ? Math.round(covered) : 0;
}

/**
 * True on a touch-first device — a phone/tablet, where auto-focusing a text
 * field would steal the screen with the keyboard. Pointer media queries alone
 * cannot be trusted for this: in-app WebViews (Messenger/Facebook et al.)
 * report a fine primary pointer on phones, which used to pop the keyboard
 * over the auth form the moment an invite link opened. `maxTouchPoints` comes
 * from the platform, so any touchscreen counts as coarse here — trading
 * composer autofocus on touchscreen laptops (harmless) for a keyboard-free
 * first paint on phones. SSR/old-browser safe (defaults to desktop behavior).
 */
export function isCoarsePointer(): boolean {
  try {
    if (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) return true;
    return (
      typeof matchMedia === 'function' &&
      matchMedia('(pointer: coarse)').matches &&
      !matchMedia('(pointer: fine)').matches
    );
  } catch {
    return false;
  }
}

/** Auto-focus the composer only on desktop-class pointers. */
export const shouldAutoFocusComposer = (): boolean => !isCoarsePointer();

/**
 * True while the layout viewport is phone-width. Tracks rotation/resize via
 * matchMedia so the value stays current across a session. Sent per turn as a
 * rendering-surface hint (like timezone/locale) so the model can format for
 * the width the answer is actually read at. SSR/old-browser safe (wide).
 */
export function useNarrowViewport(): boolean {
  return useMediaQuery('(max-width: 640px)');
}

/** True while the media query matches, tracking changes. SSR/old-browser safe (false). */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof matchMedia === 'function' && matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia(query);
    const update = () => setMatches(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [query]);
  return matches;
}

/**
 * Publish the on-screen keyboard's covered height as `--keyboard-inset` on
 * <html>, so the chat pane can pad its bottom by that amount and keep the
 * composer above the keyboard on iOS. No-op where visualViewport is absent or
 * the platform already resizes the layout (Android). Idempotent; safe to call
 * once at app mount.
 */
export function installKeyboardInset(
  windowObject: Window,
  documentObject: Document,
): (() => void) | undefined {
  const vv = windowObject.visualViewport;
  if (!vv) return;
  const root = documentObject.documentElement;

  const update = () => {
    const inset = keyboardInsetFromViewport({
      layoutHeight: windowObject.innerHeight,
      viewportHeight: vv.height,
      viewportOffsetTop: vv.offsetTop,
      viewportScale: vv.scale,
      hasTextEntryFocus: isTextEntryElement(documentObject.activeElement),
    });
    root.style.setProperty('--keyboard-inset', inset ? `${inset}px` : '0px');
  };

  let focusFrame: number | null = null;
  const onFocusIn = () => {
    if (focusFrame !== null) windowObject.cancelAnimationFrame(focusFrame);
    focusFrame = null;
    update();
  };
  const onFocusOut = () => {
    // Defer the recheck: focusout fires before the next editor receives focus.
    // A focusin in the same frame cancels this callback and preserves the lift.
    if (focusFrame !== null) windowObject.cancelAnimationFrame(focusFrame);
    focusFrame = windowObject.requestAnimationFrame(() => {
      focusFrame = null;
      update();
    });
  };

  update();
  vv.addEventListener('resize', update);
  vv.addEventListener('scroll', update);
  documentObject.addEventListener('focusin', onFocusIn);
  documentObject.addEventListener('focusout', onFocusOut);
  return () => {
    vv.removeEventListener('resize', update);
    vv.removeEventListener('scroll', update);
    documentObject.removeEventListener('focusin', onFocusIn);
    documentObject.removeEventListener('focusout', onFocusOut);
    if (focusFrame !== null) windowObject.cancelAnimationFrame(focusFrame);
    root.style.removeProperty('--keyboard-inset');
  };
}

export function useKeyboardInset(): void {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    return installKeyboardInset(window, document);
  }, []);
}
